import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiAlertCircle as FaExclamationCircle,
  FiCalendar as FaCalendarAlt,
  FiCalendar as FaCalendarCheck,
  FiCheckCircle as FaCheckCircle,
  FiClock as FaClock,
  FiEye as FaEye,
  FiPauseCircle as FaPause,
  FiRefreshCw as FaSyncAlt,
  FiRotateCcw as FaRedo,
  FiSearch as FaSearch,
  FiSend as FaPaperPlane,
  FiSlash as FaBan,
  FiSmartphone as FaMobileAlt,
  FiTrash2 as FaTrash,
  FiUsers as FaUsers,
  FiX as FaTimes,
} from 'react-icons/fi';
import api from '../api';
import { Sidebar } from '../components/Note';
import { getUserTimezone, getTimezoneMeta } from '../utils/timezone';
import '../styles/tailwind.output.css';

const STATUS_STYLE = {
  scheduled: {
    label: 'Programada',
    color: '#58a6ff',
    bg: 'rgba(88, 166, 255, 0.14)',
    icon: FaCalendarAlt,
  },
  sending: {
    label: 'En curso',
    color: '#d29922',
    bg: 'rgba(210, 153, 34, 0.14)',
    icon: FaSyncAlt,
  },
  sent: {
    label: 'Enviada',
    color: '#25d366',
    bg: 'rgba(37, 211, 102, 0.14)',
    icon: FaCheckCircle,
  },
  paused: {
    label: 'Pausada',
    color: '#94a3b8',
    bg: 'rgba(148, 163, 184, 0.14)',
    icon: FaPause,
  },
  failed: {
    label: 'Fallida',
    color: '#f85149',
    bg: 'rgba(248, 81, 73, 0.14)',
    icon: FaExclamationCircle,
  },
  canceled: {
    label: 'Cancelada',
    color: '#94a3b8',
    bg: 'rgba(148, 163, 184, 0.14)',
    icon: FaBan,
  },
};

const parseDateFlexible = (rawValue) => {
  const raw = String(rawValue || '').trim();
  if (!raw || raw.toLowerCase() === 'inmediato') return null;

  if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}/.test(raw)) {
    const parsedLocal = new Date(raw.replace(' ', 'T'));
    return Number.isNaN(parsedLocal.getTime()) ? null : parsedLocal;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeCampaignStatus = ({ rawStatus, sent, isImmediate, scheduledDate }) => {
  const s = String(rawStatus || '').trim().toLowerCase();
  if (s === 'canceled' || s === 'cancelled' || s.includes('cancel')) return 'canceled';
  if (s === 'failed' || s.includes('fall')) return 'failed';
  if (s === 'running' || s.includes('curso') || s.includes('activa') || s.includes('send')) return 'sending';
  if (s === 'done' || s.includes('complet') || s.includes('enviad')) return 'sent';
  if (s === 'paused' || s.includes('paus')) return 'paused';
  if (s === 'pending' || s.includes('pend')) return 'scheduled';

  if (Number(sent || 0) > 0) return 'sent';

  if (!isImmediate && scheduledDate instanceof Date && !Number.isNaN(scheduledDate.getTime())) {
    return scheduledDate.getTime() > Date.now() ? 'scheduled' : 'sending';
  }

  return isImmediate ? 'sent' : 'scheduled';
};

const resolveScheduleInfo = (fechaLocalRaw, fechaRaw) => {
  const local = String(fechaLocalRaw || '').trim();
  const raw = String(fechaRaw || '').trim();
  const base = local || raw;

  if (!base || raw.toLowerCase() === 'inmediato') {
    return {
      isImmediate: true,
      dateLabel: 'Inmediato',
      timeLabel: '',
      sortDate: null,
      raw: 'inmediato',
    };
  }

  const parsed = parseDateFlexible(base);
  if (parsed) {
    return {
      isImmediate: false,
      dateLabel: parsed.toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      timeLabel: parsed.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' }),
      sortDate: parsed,
      raw: base,
    };
  }

  const chunks = base.split(' ');
  return {
    isImmediate: false,
    dateLabel: chunks[0] || base,
    timeLabel: chunks[1] || '',
    sortDate: null,
    raw: base,
  };
};

function StatusBadge({ status }) {
  const key = STATUS_STYLE[status] ? status : 'scheduled';
  const cfg = STATUS_STYLE[key];
  const Icon = cfg.icon;

  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      <Icon className={status === 'sending' ? 'animate-spin' : ''} size={11} />
      {cfg.label}
    </span>
  );
}

function ProgressBar({ sent, total, color }) {
  const safeTotal = Math.max(Number(total || 0), Number(sent || 0), 1);
  const safeSent = Math.min(Math.max(Number(sent || 0), 0), safeTotal);
  const pct = Math.round((safeSent / safeTotal) * 100);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[11px] text-slate-400">
        <span>{safeSent} / {safeTotal}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-slate-800">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function MensajesCoexistencia() {
  const navigate = useNavigate();
  const [activeTimezone, setActiveTimezone] = useState(() => getUserTimezone());
  const timezoneMeta = useMemo(() => getTimezoneMeta(activeTimezone), [activeTimezone]);

  const [activeTab, setActiveTab] = useState('new');

  const [dark, setDark] = useState(() => {
    if (typeof document === 'undefined') return true;
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const sync = () => setDark(root.classList.contains('dark'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    const onStorage = (event) => {
      if (event.key === 'theme') sync();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      observer.disconnect();
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [listas, setListas] = useState([]);
  const [listaId, setListaId] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [nombreCampana, setNombreCampana] = useState('');
  const [sendSimpleText, setSendSimpleText] = useState(false);
  const [simpleTextContent, setSimpleTextContent] = useState('');

  const [varsValues, setVarsValues] = useState({});
  const [metaPhoneNumberId, setMetaPhoneNumberId] = useState('');
  const [displayPhone, setDisplayPhone] = useState('');

  const [sending, setSending] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [progress, setProgress] = useState({ total: 0, sent: 0, failed: 0 });

  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const [headerMediaUrl, setHeaderMediaUrl] = useState('');

  useEffect(() => {
    const syncTimezone = () => setActiveTimezone(getUserTimezone());
    window.addEventListener('ws:timezone_changed', syncTimezone);
    window.addEventListener('storage', syncTimezone);
    return () => {
      window.removeEventListener('ws:timezone_changed', syncTimezone);
      window.removeEventListener('storage', syncTimezone);
    };
  }, []);
  const [headerMediaType, setHeaderMediaType] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [previewMediaUrl, setPreviewMediaUrl] = useState('');
  const [previewMediaType, setPreviewMediaType] = useState('');
  const pidLockRef = useRef('');

  const [summary, setSummary] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [campaignQuery, setCampaignQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [detailId, setDetailId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detailCampaignData, setDetailCampaignData] = useState(null);
  const [detailRuns, setDetailRuns] = useState([]);

  const toAbsolute = (u) => {
    try {
      const s = String(u || '').trim();
      if (!s) return '';
      if (s.startsWith('http://') || s.startsWith('https://')) return s;
      if (s.startsWith('//')) return 'https:' + s;
      let path = s;
      if (s.startsWith('api/')) path = '/' + s;
      else if (s.startsWith('meta-templates/header-media/')) path = '/api/' + s;
      else if (!s.startsWith('/')) path = '/' + s;
      const origin = (window.location?.origin || '').replace('http://', 'https://');
      return origin + path;
    } catch {
      return String(u || '');
    }
  };

  const htmlToPlain = (html) => {
    try {
      let s = String(html || '');
      s = s.replace(/<br\s*\/?>/gi, '\n');
      s = s.replace(/<\/p>/gi, '\n');
      s = s.replace(/<p[^>]*>/gi, '');
      s = s.replace(/<[^>]+>/g, '');

      const el = document.createElement('textarea');
      el.innerHTML = s;
      s = el.value;

      s = s.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      s = s.replace(/[ \t\f\v]+/g, ' ');
      s = s.replace(/ *\n */g, '\n');
      s = s.replace(/\n{3,}/g, '\n\n');
      return s.trim();
    } catch {
      return String(html || '');
    }
  };

  const buildPreviewText = (tpl, vars) => {
    try {
      const raw = String(tpl?.body_text || '');
      let plain = htmlToPlain(raw);
      plain = plain.replace(/\{\{(\d+)\}\}/g, (_, n) => {
        const v = vars[Number(n)] || `{{${n}}}`;
        return String(v);
      });
      return plain;
    } catch {
      return '';
    }
  };

  const applySampleVars = (text) => {
    let s = String(text || '');
    s = s.replace(/\{\{\s*nombre\s*\}\}/gi, 'Juan');
    s = s.replace(/\{\{1\}\}/g, 'Juan');
    s = s.replace(/\{\{2\}\}/g, 'mañana');
    s = s.replace(/\{\{3\}\}/g, '10:00');
    return s;
  };

  const extractFirstName = (fullName) => {
    const clean = String(fullName || '').trim().replace(/\s+/g, ' ');
    if (!clean) return 'Cliente';
    return clean.split(' ')[0] || 'Cliente';
  };

  const varsCount = useMemo(() => {
    try {
      const body = selectedTemplate?.body_text || '';
      const re = /\{\{(\d+)\}\}/g;
      let maxIdx = 0;
      let m;
      while ((m = re.exec(body)) !== null) {
        const n = parseInt(m[1], 10);
        if (!Number.isNaN(n) && n > maxIdx) maxIdx = n;
      }
      return maxIdx;
    } catch {
      return 0;
    }
  }, [selectedTemplate]);

  const usesNombreAliasInTemplate = useMemo(() => {
    try {
      const body = String(selectedTemplate?.body_text || '');
      return /\{\{\s*nombre\s*\}\}/i.test(body);
    } catch {
      return false;
    }
  }, [selectedTemplate]);

  const buildVariablesForContact = (contact, forSchedule = false) => {
    const vars = Array.from({ length: varsCount }, (_, idx) => String(varsValues[idx + 1] || ''));
    if (!vars.length) return vars;

    const firstName = extractFirstName(contact?.full_name);
    const shouldAutoName = usesNombreAliasInTemplate || !String(vars[0] || '').trim();
    if (shouldAutoName) {
      vars[0] = forSchedule ? '__FIRST_NAME__' : firstName;
    }
    return vars;
  };

  const loadCampaignData = useCallback(async () => {
    setSummaryLoading(true);
    const [summaryResp, campaignsResp] = await Promise.allSettled([
      api.get('campanas/summary/'),
      api.get('campanas/'),
    ]);

    if (summaryResp.status === 'fulfilled') {
      const data = summaryResp.value?.data;
      const arr = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
      setSummary(arr);
    } else {
      setSummary([]);
    }

    if (campaignsResp.status === 'fulfilled') {
      setAllCampaigns(Array.isArray(campaignsResp.value?.data) ? campaignsResp.value.data : []);
    } else {
      setAllCampaigns([]);
    }

    setSummaryLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data } = await api.get('integration/connection-status/');
        if (cancelled) return;
        const d = data?.details || {};
        if (data?.is_connected && d.phone_number_id) {
          const pid = String(d.phone_number_id);
          pidLockRef.current = pid;
          setMetaPhoneNumberId(pid);
          if (d.display_phone_number) setDisplayPhone(d.display_phone_number);
          try {
            window.localStorage.setItem('meta_phone_number_id', pid);
          } catch {
            // noop
          }
        }
      } catch {
        // noop
      }

      try {
        const { data } = await api.get('integration/meta-my-connection/');
        if (cancelled) return;
        const nums = Array.isArray(data?.numbers) ? data.numbers : [];
        if (nums.length) {
          const match = nums.find((n) => String(n.phone_number_id) === String(pidLockRef.current)) || nums[0];
          if (match?.display_phone_number) setDisplayPhone(match.display_phone_number);
          if (!pidLockRef.current && match?.phone_number_id) {
            const pid = String(match.phone_number_id);
            pidLockRef.current = pid;
            setMetaPhoneNumberId(pid);
            try {
              window.localStorage.setItem('meta_phone_number_id', pid);
            } catch {
              // noop
            }
          }
        }
      } catch {
        // noop
      }

      try {
        const { data } = await api.get('meta-templates/');
        if (cancelled) return;
        const arr = Array.isArray(data)
          ? data.filter((t) => String(t.status || '').trim().toUpperCase() === 'APPROVED')
          : [];
        setTemplates(arr);
      } catch {
        if (!cancelled) setTemplates([]);
      }

      try {
        const { data } = await api.get('listas/');
        if (cancelled) return;
        setListas(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setListas([]);
      }

      if (!cancelled) {
        await loadCampaignData();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadCampaignData]);

  useEffect(() => {
    const tpl = templates.find((t) => String(t.id) === String(selectedTemplateId));
    setSelectedTemplate(tpl || null);
    setVarsValues({});

    if (!tpl) {
      setHeaderMediaUrl('');
      setHeaderMediaType('');
      setPreviewMediaUrl('');
      setPreviewMediaType('');
      setPreviewText('');
      return;
    }

    const ht = String(tpl.header_type || '').toUpperCase();
    const savedUrl = String(tpl.header_media_url || '').trim();
    const body = String(tpl.body_text || '');
    const mVid = body.match(/<video[^>]+src="([^"]+)"/i);
    const mImg = body.match(/<img[^>]+src="([^"]+)"/i);

    let mediaUrl = savedUrl;
    let mediaType = ht;
    if (!mediaUrl && mVid && mVid[1]) {
      mediaUrl = mVid[1];
      mediaType = 'VIDEO';
    } else if (!mediaUrl && mImg && mImg[1]) {
      mediaUrl = mImg[1];
      mediaType = 'IMAGE';
    }

    if (!mediaType && mediaUrl) {
      const lower = mediaUrl.toLowerCase();
      mediaType = lower.match(/\.pdf|\.docx?|\.xlsx?$/)
        ? 'DOCUMENT'
        : (lower.match(/\.mp4|\.mov|\.webm|\.m4v$/) ? 'VIDEO' : 'IMAGE');
    }

    setHeaderMediaType(mediaType);
    setHeaderMediaUrl(mediaUrl);
    setPreviewMediaType(mediaType);
    setPreviewMediaUrl(toAbsolute(mediaUrl));
    setPreviewText(buildPreviewText(tpl, {}));
  }, [selectedTemplateId, templates]);

  useEffect(() => {
    setPreviewText(buildPreviewText(selectedTemplate, varsValues));
  }, [selectedTemplate, varsValues]);

  const loadContactsFromList = useCallback(async (listId) => {
    if (!listId) {
      setContacts([]);
      return;
    }
    setLoadingContacts(true);
    try {
      const { data } = await api.get(`contactos/by-list/${listId}/`);
      setContacts(Array.isArray(data) ? data : []);
    } catch {
      setContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  }, []);

  useEffect(() => {
    loadContactsFromList(listaId);
  }, [listaId, loadContactsFromList]);

  const disabled = useMemo(() => {
    const base = (
      !listaId
      || contacts.length === 0
      || sending
      || !String(metaPhoneNumberId || '').trim()
    );

    if (sendSimpleText) {
      return base || !String(simpleTextContent || '').trim();
    }

    return base || !String(nombreCampana || '').trim() || !selectedTemplate;
  }, [listaId, contacts.length, sending, metaPhoneNumberId, sendSimpleText, simpleTextContent, nombreCampana, selectedTemplate]);

  const scheduleDisabled = useMemo(() => {
    const base = (
      !listaId
      || scheduling
      || !String(metaPhoneNumberId || '').trim()
      || !String(scheduledDate || '').trim()
      || !String(scheduledTime || '').trim()
    );
    return base || sendSimpleText || !String(nombreCampana || '').trim() || !selectedTemplate;
  }, [listaId, scheduling, metaPhoneNumberId, scheduledDate, scheduledTime, sendSimpleText, nombreCampana, selectedTemplate]);

  const handleSend = async () => {
    if (disabled) return;

    const total = contacts.length;
    const runId = `meta-${Date.now()}`;
    let firstError = '';

    setProgress({ total, sent: 0, failed: 0 });
    setSending(true);

    try {
      for (let i = 0; i < contacts.length; i += 1) {
        const c = contacts[i];

        try {
          const payload = sendSimpleText
            ? {
                to: String(c.phone || '').trim(),
                meta_phone_number_id: String(metaPhoneNumberId || '').trim(),
                content: String(simpleTextContent || '').trim(),
                campaign_name: String(nombreCampana || '').trim(),
                list_id: String(listaId || ''),
                contact_id: c.id,
                run_id: runId,
                timezone: activeTimezone,
              }
            : {
                to: String(c.phone || '').trim(),
                template_name: selectedTemplate.name,
                language: selectedTemplate.language || 'es',
                meta_phone_number_id: String(metaPhoneNumberId || '').trim(),
                variables: buildVariablesForContact(c, false),
                campaign_name: String(nombreCampana || '').trim(),
                list_id: String(listaId || ''),
                contact_id: c.id,
                run_id: runId,
                timezone: activeTimezone,
              };

          const needsHeaderMedia = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(String(selectedTemplate?.header_type || '').toUpperCase());
          if (!sendSimpleText && needsHeaderMedia) {
            const link = String(headerMediaUrl || selectedTemplate?.header_media_url || '').trim();
            if (!link) {
              alert('Esta plantilla requiere encabezado con media. Adjunta o pega un enlace antes de enviar.');
              setSending(false);
              return;
            }
            payload.header_media_url = link;
            if (headerMediaType) payload.header_media_type = headerMediaType;
          }

          const res = await api.post('messages/send-meta/', payload, { timeout: 20000 });
          if (res.status >= 200 && res.status < 300 && res.data?.success) {
            setProgress((p) => ({ ...p, sent: p.sent + 1 }));
          } else {
            if (!firstError) {
              firstError = String(res.data?.error || res.data?.detail || 'Fallo al enviar mensaje');
            }
            setProgress((p) => ({ ...p, failed: p.failed + 1 }));
          }
        } catch (err) {
          if (!firstError) {
            firstError = String(err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'Error de envío');
          }
          setProgress((p) => ({ ...p, failed: p.failed + 1 }));
        }
      }

      if (firstError) {
        alert(`Envío finalizado con incidencias: ${firstError}`);
      } else {
        alert('Envío finalizado');
      }

      await loadCampaignData();
      setActiveTab('scheduled');
    } finally {
      setSending(false);
    }
  };

  const handleSchedule = async () => {
    if (scheduleDisabled) return;

    try {
      setScheduling(true);

      const needsHeaderMedia = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(String(selectedTemplate?.header_type || '').toUpperCase());
      const link = String(headerMediaUrl || selectedTemplate?.header_media_url || '').trim();
      if (needsHeaderMedia && !link) {
        alert('Esta plantilla requiere medio en el encabezado. Agrega enlace o sube archivo antes de programar.');
        setScheduling(false);
        return;
      }

      const localDateTime = `${scheduledDate}T${scheduledTime}`;
      const scheduledDateObj = new Date(localDateTime);
      if (Number.isNaN(scheduledDateObj.getTime())) {
        alert('La fecha/hora programada no es válida.');
        setScheduling(false);
        return;
      }
      const iso = scheduledDateObj.toISOString();

      const payload = {
        nombre: String(nombreCampana || '').trim(),
        lista_id: String(listaId || '').trim(),
        template_name: selectedTemplate.name,
        language: selectedTemplate.language || 'es',
        variables: buildVariablesForContact(null, true),
        header_media_url: link || undefined,
        meta_phone_number_id: String(metaPhoneNumberId || '').trim(),
        scheduled_at: iso,
        timezone: activeTimezone,
      };

      const res = await api.post('campanas/schedule-coex/', payload, { timeout: 20000 });
      if (res.status >= 200 && res.status < 300) {
        alert('Campaña programada correctamente.');
        setScheduledDate('');
        setScheduledTime('');
        await loadCampaignData();
        setActiveTab('scheduled');
      } else {
        alert('No se pudo programar la campaña.');
      }
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      alert(`Error al programar: ${status || ''} ${data?.error || data?.detail || ''}`);
    } finally {
      setScheduling(false);
    }
  };

  const pickAndUploadHeaderMedia = async (expectedType) => {
    return new Promise((resolve) => {
      try {
        const input = document.createElement('input');
        input.type = 'file';
        if (expectedType === 'IMAGE') input.accept = 'image/*';
        else if (expectedType === 'VIDEO') input.accept = 'video/*';
        else input.accept = '*/*';

        input.onchange = async () => {
          try {
            const file = input.files && input.files[0];
            if (!file) return resolve({ url: '', type: '' });
            const fd = new FormData();
            fd.append('file', file);
            const resp = await api.post('meta-templates/upload-header-media/', fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            resolve({ url: resp.data?.url || '', type: String(resp.data?.type || '').toUpperCase() });
          } catch (e) {
            alert('Fallo al subir archivo: ' + (e?.response?.data?.detail || e?.message || 'error'));
            resolve({ url: '', type: '' });
          }
        };

        input.click();
      } catch {
        resolve({ url: '', type: '' });
      }
    });
  };

  const listsMap = useMemo(() => {
    const m = new Map();
    listas.forEach((l) => m.set(String(l.id), l));
    return m;
  }, [listas]);

  const templatesMap = useMemo(() => {
    const m = new Map();
    templates.forEach((t) => {
      if (t?.name) m.set(String(t.name).toLowerCase(), t);
    });
    return m;
  }, [templates]);

  const campaignRows = useMemo(() => {
    const campaignsById = new Map();
    allCampaigns.forEach((c) => campaignsById.set(String(c.id), c));

    const summaryById = new Map();
    summary.forEach((s) => summaryById.set(String(s.id), s));

    const ids = new Set([...Array.from(campaignsById.keys()), ...Array.from(summaryById.keys())]);
    const rows = [];

    ids.forEach((id) => {
      const c = campaignsById.get(id) || {};
      const s = summaryById.get(id) || {};

      const appearsInSummary = summaryById.has(id);
      const rawPlantillas = c?.plantillas;
      const isObjectTemplate = !!rawPlantillas && typeof rawPlantillas === 'object' && !Array.isArray(rawPlantillas);
      const hasCoexMetadata = isObjectTemplate
        && (
          rawPlantillas?.template_name
          || rawPlantillas?.meta_phone_number_id
          || rawPlantillas?.header_media_url
          || Array.isArray(rawPlantillas?.variables)
        );
      // Evita mezclar campañas del flujo no-oficial en la vista de coexistencia.
      if (!appearsInSummary && !hasCoexMetadata) {
        return;
      }

      const nombre = String(c.nombre || s.nombre || `Campaña ${id}`);
      const plantillas = c.plantillas;
      let templateName = '';
      let templateLanguage = '';

      if (Array.isArray(plantillas)) {
        templateName = String(plantillas[0] || '');
      } else if (plantillas && typeof plantillas === 'object') {
        templateName = String(plantillas.template_name || plantillas.name || '');
        templateLanguage = String(plantillas.language || '');
      } else if (typeof plantillas === 'string') {
        templateName = String(plantillas);
      }

      const tpl = templatesMap.get(templateName.toLowerCase());
      const templateBody = htmlToPlain(String(tpl?.body_text || tpl?.body_text_html || ''));

      const listIdRaw = String(c.clientes || '').trim();
      const listObj = listsMap.get(listIdRaw);
      const listName = String(s.vista_nombre || listObj?.name || (listIdRaw ? `Lista ${listIdRaw}` : '-'));

      const sent = Number(s.alcance || 0);
      const contactCount = Array.isArray(listObj?.contacts) ? listObj.contacts.length : 0;
      const schedule = resolveScheduleInfo(s.fecha_local || c.fecha_local || '', s.fecha || c.fecha || '');

      const status = normalizeCampaignStatus({
        rawStatus: s.estado || c.estado,
        sent,
        isImmediate: schedule.isImmediate,
        scheduledDate: schedule.sortDate,
      });

      const total = Math.max(sent, contactCount, sent > 0 ? sent : 0);
      const failed = status === 'failed' ? Math.max(total - sent, 0) : 0;
      const createdAtRaw = String(c.created_at || '').trim();
      const createdAtDate = parseDateFlexible(createdAtRaw) || (createdAtRaw ? new Date(createdAtRaw) : null);
      const createdAtValid = createdAtDate && !Number.isNaN(createdAtDate.getTime());

      rows.push({
        id,
        nombre,
        templateName: templateName || 'Texto simple',
        templateLanguage,
        templateBody,
        listName,
        listId: listIdRaw,
        contactCount,
        sent,
        failed,
        total: total || 0,
        schedule,
        status,
        createdAtLabel: createdAtValid
          ? createdAtDate.toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' })
          : '',
        createdAtTs: createdAtValid ? createdAtDate.getTime() : 0,
      });
    });

    rows.sort((a, b) => {
      const tA = a.schedule.sortDate ? a.schedule.sortDate.getTime() : 0;
      const tB = b.schedule.sortDate ? b.schedule.sortDate.getTime() : 0;
      if (tA !== tB) return tB - tA;
      if (a.createdAtTs !== b.createdAtTs) return b.createdAtTs - a.createdAtTs;
      return Number(b.id) - Number(a.id);
    });

    return rows;
  }, [allCampaigns, summary, listsMap, templatesMap]);

  const filteredRows = useMemo(() => {
    const q = String(campaignQuery || '').trim().toLowerCase();
    return campaignRows.filter((c) => {
      const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
      const matchesQuery = !q
        || c.nombre.toLowerCase().includes(q)
        || c.templateName.toLowerCase().includes(q)
        || c.listName.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [campaignRows, campaignQuery, statusFilter]);

  const counters = useMemo(() => ({
    scheduled: campaignRows.filter((c) => c.status === 'scheduled').length,
    sending: campaignRows.filter((c) => c.status === 'sending').length,
    sent: campaignRows.filter((c) => c.status === 'sent').length,
    canceled: campaignRows.filter((c) => c.status === 'canceled').length,
  }), [campaignRows]);

  const openDetail = async (campaignId) => {
    setDetailId(String(campaignId));
    setDetailLoading(true);
    setDetailError('');
    setDetailRuns([]);

    try {
      const { data } = await api.get(`campanas/${campaignId}/envios/`);
      setDetailCampaignData(data?.campaign || null);
      setDetailRuns(Array.isArray(data?.runs) ? data.runs : []);
    } catch (err) {
      setDetailCampaignData(null);
      setDetailError(String(err?.response?.data?.detail || err?.message || 'No se pudo cargar el detalle.'));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetailLoading(false);
    setDetailError('');
    setDetailCampaignData(null);
    setDetailRuns([]);
  };

  const removeCampaign = async (campaignId) => {
    if (!window.confirm('¿Cancelar y eliminar esta campaña?')) return;
    try {
      await api.delete(`campanas/delete/${campaignId}/`);
      if (String(detailId) === String(campaignId)) closeDetail();
      await loadCampaignData();
    } catch (err) {
      alert(String(err?.response?.data?.detail || 'No se pudo eliminar la campaña'));
    }
  };

  const cancelCampaign = async (campaignId) => {
    if (!window.confirm('¿Cancelar esta campaña? No se enviará y quedará guardada para revisar o reenviar más tarde.')) return;
    try {
      await api.post(`campanas/${campaignId}/cancel/`);
      await loadCampaignData();
    } catch (err) {
      alert(String(err?.response?.data?.detail || 'No se pudo cancelar la campaña'));
    }
  };

  const resendCampaign = async (campaignId) => {
    if (!window.confirm('¿Reenviar esta campaña? Se reactivará según su programación (verifica que su lista tenga contactos).')) return;
    try {
      await api.post(`campanas/${campaignId}/resend/`);
      await loadCampaignData();
    } catch (err) {
      alert(String(err?.response?.data?.detail || 'No se pudo reenviar la campaña'));
    }
  };

  const selectedRow = useMemo(
    () => campaignRows.find((r) => String(r.id) === String(detailId)) || null,
    [campaignRows, detailId],
  );

  const fieldClass = dark
    ? 'w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-sky-400 [color-scheme:dark]'
    : 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-sky-500 [color-scheme:light]';
  
  const labelClass = dark
    ? 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400'
    : 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-600';

  return (
    <div className="ws-page ws-page-mensajes flex min-h-screen bg-slate-950 text-slate-100">
      <Sidebar />

      <main className="flex-1 px-4 py-6 md:px-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <header className="ws-header-inline ws-header-surface flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold">Campañas Coexistencia</h1>
              <p className="mt-1 text-sm text-slate-400">
                Diseña, envía y programa campañas con tu número oficial conectado en Meta.
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate('/calendario')}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 transition hover:border-sky-500 hover:text-sky-300"
              title="Ver calendario"
            >
              <FaCalendarCheck size={13} />
              Calendario
            </button>
          </header>

          <div className="inline-flex items-center gap-1 rounded-lg border border-slate-800 bg-slate-900 p-1">
            {[
              { id: 'new', label: 'Nueva campaña', icon: FaPaperPlane },
              { id: 'scheduled', label: 'Campañas programadas', icon: FaCalendarAlt },
            ].map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition"
                  style={{
                    background: active ? 'rgba(88, 166, 255, 0.16)' : 'transparent',
                    color: active ? '#93c5fd' : '#94a3b8',
                  }}
                >
                  <Icon size={13} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {activeTab === 'new' ? (
            <section className="space-y-5">
              <div className="rounded-xl border border-sky-600/40 bg-sky-500/10 p-4 text-sm text-slate-200">
                <div className="flex items-start gap-3">
                  <FaExclamationCircle className="mt-0.5 text-sky-300" />
                  <p>
                    Envía mensajes usando tu conexión oficial de Meta (Coexistencia). Selecciona una{' '}
                    <span className="font-bold text-emerald-300">plantilla APROBADA</span>, una lista de clientes y, opcionalmente,
                    programa la fecha de envío.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-5">
                  <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-300">Configuración</h2>

                  <div className="mb-5 flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900 px-3 py-2.5">
                    <div>
                      <p className="text-sm font-medium text-slate-100">Enviar texto simple</p>
                      <p className="text-xs text-slate-400">Desactiva plantilla y variables</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSendSimpleText((v) => !v)}
                      className="relative h-6 w-11 rounded-full transition-colors"
                      style={{ background: sendSimpleText ? '#25d366' : '#334155' }}
                    >
                      <span
                        className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all"
                        style={{ left: sendSimpleText ? '22px' : '2px' }}
                      />
                    </button>
                  </div>

                  <div className="mb-4">
                    <label className={labelClass}>Nombre de campaña</label>
                    <input
                      type="text"
                      value={nombreCampana}
                      onChange={(e) => setNombreCampana(e.target.value)}
                      disabled={sendSimpleText}
                      className={`${fieldClass} disabled:cursor-not-allowed disabled:opacity-60`}
                      placeholder="Ej. Black Friday 2026"
                    />
                  </div>

                  <div className="mb-4">
                    <label className={labelClass}>Número conectado</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={metaPhoneNumberId}
                        readOnly
                        className={dark 
                          ? "w-full cursor-not-allowed rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 pr-36 text-sm text-slate-300"
                          : "w-full cursor-not-allowed rounded-lg border border-slate-300 bg-slate-100 px-3 py-2.5 pr-36 text-sm text-slate-700"
                        }
                        placeholder="Se autocompleta desde la conexión activa"
                      />
                      {displayPhone && (
                        <span className="pointer-events-none absolute inset-y-0 right-2 inline-flex items-center rounded-full bg-emerald-500/20 px-2.5 text-xs font-semibold text-emerald-300">
                          {displayPhone}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className={labelClass}>Plantilla aprobada</label>
                    <div className="relative">
                      <select
                        value={selectedTemplateId}
                        onChange={(e) => setSelectedTemplateId(e.target.value)}
                        disabled={sendSimpleText}
                        className={`${fieldClass} appearance-none pr-10 disabled:cursor-not-allowed disabled:opacity-60`}
                      >
                        <option value="">Seleccione su plantilla aprobada...</option>
                        {templates.map((t) => (
                          <option key={t.id} value={t.id}>{t.name} - {t.language}</option>
                        ))}
                      </select>
                      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-500">▼</span>
                    </div>
                  </div>

                  {selectedTemplate && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(String(selectedTemplate.header_type || '').toUpperCase()) && (
                    <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
                      <p className="mb-2 text-xs text-amber-200">
                        Esta plantilla requiere media en el encabezado ({String(selectedTemplate.header_type).toUpperCase()}).
                      </p>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <input
                          type="text"
                          value={headerMediaUrl}
                          onChange={(e) => {
                            setHeaderMediaUrl(e.target.value);
                            setPreviewMediaUrl(toAbsolute(e.target.value));
                          }}
                          placeholder="https://..."
                          className="min-w-0 flex-1 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none"
                        />
                        <button
                          type="button"
                          onClick={async () => {
                            const picked = await pickAndUploadHeaderMedia(String(selectedTemplate.header_type || '').toUpperCase());
                            if (picked.url) {
                              setHeaderMediaUrl(picked.url);
                              if (picked.type) setHeaderMediaType(picked.type);
                              setPreviewMediaUrl(toAbsolute(picked.url));
                              setPreviewMediaType(picked.type || previewMediaType);
                            }
                          }}
                          className="rounded-lg border border-sky-500/50 bg-sky-500/20 px-3 py-2 text-sm font-medium text-sky-200 hover:bg-sky-500/30"
                        >
                          Subir archivo
                        </button>
                      </div>
                    </div>
                  )}

                  {sendSimpleText && (
                    <div className="mb-4">
                      <label className={labelClass}>Texto simple</label>
                      <textarea
                        value={simpleTextContent}
                        onChange={(e) => setSimpleTextContent(e.target.value)}
                        rows={3}
                        className={fieldClass}
                        placeholder="Escribe el mensaje a enviar..."
                      />
                    </div>
                  )}

                  <div className="mb-2">
                    <label className={labelClass}>Lista de clientes</label>
                    <select
                      value={listaId}
                      onChange={(e) => setListaId(e.target.value)}
                      className={fieldClass}
                    >
                      <option value="">Seleccione una lista...</option>
                      {listas.map((l) => (
                        <option key={l.id} value={l.id}>{l.name}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-slate-400">
                      {loadingContacts ? 'Cargando contactos...' : `${contacts.length} contactos seleccionados`}
                    </p>
                  </div>

                  {varsCount > 0 && !sendSimpleText && (
                    <div className="mb-4 rounded-lg border border-slate-800 bg-slate-900 p-3">
                      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Variables</h3>
                      <div className="space-y-2">
                        {Array.from({ length: varsCount }, (_, i) => i + 1).map((n) => (
                          <div key={n}>
                            <label className="mb-1 block text-[11px] text-slate-500">{`{{${n}}}`}</label>
                            <input
                              type="text"
                              value={varsValues[n] || ''}
                              onChange={(e) => setVarsValues((v) => ({ ...v, [n]: e.target.value }))}
                              className="w-full rounded-md border border-slate-700 bg-slate-950 px-2.5 py-2 text-sm text-slate-100"
                              placeholder={n === 1 ? 'Vacío = primer nombre del contacto' : `Valor para {{${n}}}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mb-4">
                    <label className={labelClass}>Programar envío (opcional)</label>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        disabled={sendSimpleText}
                        className={`${fieldClass} disabled:cursor-not-allowed disabled:opacity-60`}
                      />
                      <input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        disabled={sendSimpleText}
                        className={`${fieldClass} disabled:cursor-not-allowed disabled:opacity-60`}
                      />
                    </div>
                    <p className="mt-1 text-xs text-slate-500">TZ: {timezoneMeta.label}</p>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={handleSend}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-sky-500/50 bg-sky-500/15 px-4 py-2.5 text-sm font-semibold text-sky-200 transition hover:bg-sky-500/25 disabled:cursor-not-allowed disabled:border-slate-700 disabled:bg-slate-800 disabled:text-slate-500"
                    >
                      {sending ? <FaSyncAlt className="animate-spin" size={13} /> : <FaPaperPlane size={13} />}
                      {sending ? 'Enviando...' : 'Enviar ahora'}
                    </button>

                    <button
                      type="button"
                      disabled={scheduleDisabled}
                      onClick={handleSchedule}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:bg-slate-800 disabled:text-slate-500"
                    >
                      {scheduling ? <FaSyncAlt className="animate-spin" size={13} /> : <FaCalendarAlt size={13} />}
                      {scheduling ? 'Programando...' : 'Programar campaña'}
                    </button>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-5">
                  <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-300">Vista previa</h2>

                  {selectedTemplate ? (
                    <div className="space-y-4">
                      <div className="rounded-xl border border-slate-800 bg-slate-900 px-3 py-2 text-xs text-slate-400">
                        <p>
                          Plantilla: <span className="font-semibold text-emerald-300">{selectedTemplate.name}</span>{' '}
                          ({selectedTemplate.language})
                        </p>
                      </div>

                      <div className="mx-auto w-full max-w-xs overflow-hidden rounded-2xl border-4 border-slate-700">
                        <div className="flex items-center gap-2 bg-[#0b4f44] px-3 py-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-400 text-xs font-bold text-slate-900">
                            W
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-white">WaveSales</p>
                            <p className="text-[10px] text-emerald-100/80">Negocio verificado</p>
                          </div>
                        </div>

                        <div className="bg-[#0a1a15] p-3">
                          {previewMediaUrl && previewMediaType === 'IMAGE' && (
                            <img src={previewMediaUrl} alt="header" className="mb-2 w-full rounded-lg object-cover" />
                          )}

                          {previewMediaUrl && previewMediaType === 'VIDEO' && (
                            <video src={previewMediaUrl} controls className="mb-2 w-full rounded-lg" />
                          )}

                          {previewMediaUrl && previewMediaType === 'DOCUMENT' && (
                            <a
                              href={previewMediaUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="mb-2 block rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-xs text-sky-300"
                            >
                              Documento adjunto
                            </a>
                          )}

                          <div className="ml-auto max-w-[90%] rounded-xl bg-[#005c4b] px-3 py-2 text-xs text-white">
                            <p className="whitespace-pre-wrap break-words leading-relaxed">{previewText}</p>
                            <p className="mt-1 text-right text-[10px] text-white/60">
                              {new Date().toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })} ✓✓
                            </p>
                          </div>
                        </div>
                      </div>

                      {scheduledDate && scheduledTime && (
                        <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs text-sky-200">
                          Programada para {scheduledDate} a las {scheduledTime} ({timezoneMeta.short})
                        </div>
                      )}

                      <div className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-xs text-slate-400">
                        <p>
                          Progreso actual: <span className="font-semibold text-slate-200">{progress.sent}/{progress.total}</span>{' '}
                          enviados, <span className="font-semibold text-rose-300">{progress.failed}</span> fallidos.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex min-h-[360px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-slate-700 bg-slate-900 text-slate-500">
                      <FaMobileAlt size={24} />
                      <p className="text-sm">Selecciona una plantilla para ver la vista previa.</p>
                    </div>
                  )}
                </div>
              </div>
            </section>
          ) : (
            <section className="space-y-5">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                {[
                  {
                    label: 'Programadas',
                    value: counters.scheduled,
                    color: '#58a6ff',
                    bg: 'rgba(88, 166, 255, 0.12)',
                    icon: FaCalendarAlt,
                  },
                  {
                    label: 'En curso',
                    value: counters.sending,
                    color: '#d29922',
                    bg: 'rgba(210, 153, 34, 0.12)',
                    icon: FaSyncAlt,
                  },
                  {
                    label: 'Enviadas',
                    value: counters.sent,
                    color: '#25d366',
                    bg: 'rgba(37, 211, 102, 0.12)',
                    icon: FaCheckCircle,
                  },
                ].map((m) => {
                  const Icon = m.icon;
                  return (
                    <div
                      key={m.label}
                      className="flex items-center gap-3 rounded-xl border border-transparent p-4"
                      style={{ background: m.bg }}
                    >
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-lg"
                        style={{ background: 'rgba(2, 6, 23, 0.5)', color: m.color }}
                      >
                        <Icon className={m.label === 'En curso' ? 'animate-spin' : ''} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold" style={{ color: m.color }}>{m.value}</p>
                        <p className="text-xs text-slate-400">{m.label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2">
                  <FaSearch className="text-slate-500" size={13} />
                  <input
                    type="text"
                    value={campaignQuery}
                    onChange={(e) => setCampaignQuery(e.target.value)}
                    placeholder="Buscar por campaña, plantilla o lista..."
                    className="w-full bg-transparent text-sm text-slate-100 outline-none placeholder:text-slate-500"
                  />
                  {campaignQuery && (
                    <button type="button" onClick={() => setCampaignQuery('')} className="text-slate-500 hover:text-slate-300">
                      <FaTimes size={12} />
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-1 rounded-lg border border-slate-700 bg-slate-900 p-1">
                  {[
                    { id: 'all', label: 'Todas' },
                    { id: 'scheduled', label: 'Programadas' },
                    { id: 'sending', label: 'En curso' },
                    { id: 'sent', label: 'Enviadas' },
                    { id: 'failed', label: 'Fallidas' },
                    { id: 'canceled', label: 'Canceladas' },
                  ].map((filter) => (
                    <button
                      key={filter.id}
                      type="button"
                      onClick={() => setStatusFilter(filter.id)}
                      className="rounded-md px-3 py-1.5 text-xs font-semibold transition"
                      style={{
                        background: statusFilter === filter.id ? 'rgba(88, 166, 255, 0.18)' : 'transparent',
                        color: statusFilter === filter.id ? '#bae6fd' : '#94a3b8',
                      }}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={loadCampaignData}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-300 transition hover:border-sky-500 hover:text-sky-300"
                  title="Refrescar campañas"
                >
                  <FaSyncAlt size={12} />
                  Refrescar
                </button>
              </div>

              <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/70">
                {summaryLoading ? (
                  <div className="flex items-center gap-2 p-5 text-sm text-slate-400">
                    <FaSyncAlt className="animate-spin" />
                    Cargando campañas...
                  </div>
                ) : filteredRows.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 p-16 text-slate-500">
                    <FaPaperPlane size={24} />
                    <p className="text-sm">No se encontraron campañas con esos filtros.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-900">
                        <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-500">
                          <th className="px-4 py-3">Campaña</th>
                          <th className="px-4 py-3">Plantilla</th>
                          <th className="px-4 py-3">Lista</th>
                          <th className="px-4 py-3">Contactos</th>
                          <th className="px-4 py-3">Envío</th>
                          <th className="px-4 py-3">Estado</th>
                          <th className="px-4 py-3">Progreso</th>
                          <th className="px-4 py-3 text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.map((c) => (
                          <tr key={c.id} className="border-b border-slate-800/80 text-slate-200 hover:bg-slate-800/30">
                            <td className="px-4 py-3 align-top">
                              <button
                                type="button"
                                onClick={() => openDetail(c.id)}
                                className="text-left"
                              >
                                <p className="font-semibold hover:text-sky-300">{c.nombre}</p>
                                <p className="text-xs text-slate-500">{c.createdAtLabel ? `Creada ${c.createdAtLabel}` : `ID ${c.id}`}</p>
                              </button>
                            </td>

                            <td className="px-4 py-3 align-top">
                              <span className="rounded bg-slate-800 px-2 py-1 font-mono text-xs text-slate-200">
                                {c.templateName}
                              </span>
                              {c.templateLanguage && (
                                <p className="mt-1 text-[11px] text-slate-500">{c.templateLanguage}</p>
                              )}
                            </td>

                            <td className="px-4 py-3 align-top text-slate-300">{c.listName}</td>

                            <td className="px-4 py-3 align-top">
                              <div className="inline-flex items-center gap-1.5 text-slate-200">
                                <FaUsers size={11} className="text-slate-500" />
                                <span className="font-mono text-xs">{c.contactCount || c.sent || 0}</span>
                              </div>
                            </td>

                            <td className="px-4 py-3 align-top">
                              {c.schedule.isImmediate ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-300">
                                  <FaPaperPlane size={10} />
                                  Inmediato
                                </span>
                              ) : (
                                <div>
                                  <p>{c.schedule.dateLabel}</p>
                                  {c.schedule.timeLabel && (
                                    <p className="text-xs text-slate-500">
                                      <FaClock className="mr-1 inline" size={10} />
                                      {c.schedule.timeLabel} ({timezoneMeta.short})
                                    </p>
                                  )}
                                </div>
                              )}
                            </td>

                            <td className="px-4 py-3 align-top">
                              <StatusBadge status={c.status} />
                            </td>

                            <td className="min-w-[140px] px-4 py-3 align-top">
                              {c.status === 'scheduled' && c.sent === 0 ? (
                                <p className="text-xs text-slate-500">Pendiente de ejecución</p>
                              ) : (
                                <ProgressBar sent={c.sent} total={c.total || c.sent || 1} color={STATUS_STYLE[c.status]?.color || '#58a6ff'} />
                              )}
                            </td>

                            <td className="px-4 py-3 align-top text-right">
                              <div className="inline-flex items-center gap-1">
                                <button
                                  type="button"
                                  title="Ver detalle"
                                  onClick={() => openDetail(c.id)}
                                  className="rounded p-1.5 text-sky-300 transition hover:bg-sky-500/15"
                                >
                                  <FaEye size={12} />
                                </button>

                                {(c.status === 'scheduled' || c.status === 'sending') && (
                                  <button
                                    type="button"
                                    title="Cancelar (no envía; queda para revisar/reenviar)"
                                    onClick={() => cancelCampaign(c.id)}
                                    className="rounded p-1.5 text-amber-300 transition hover:bg-amber-500/15"
                                  >
                                    <FaBan size={12} />
                                  </button>
                                )}

                                {c.status === 'canceled' && (
                                  <button
                                    type="button"
                                    title="Reenviar (reactivar según su programación)"
                                    onClick={() => resendCampaign(c.id)}
                                    className="rounded p-1.5 text-emerald-300 transition hover:bg-emerald-500/15"
                                  >
                                    <FaRedo size={12} />
                                  </button>
                                )}

                                {(c.status === 'scheduled' || c.status === 'canceled') && (
                                  <button
                                    type="button"
                                    title="Eliminar campaña"
                                    onClick={() => removeCampaign(c.id)}
                                    className="rounded p-1.5 text-rose-300 transition hover:bg-rose-500/15"
                                  >
                                    <FaTrash size={12} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </main>

      {detailId && selectedRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-3"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDetail();
          }}
        >
          <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-slate-700 bg-slate-900">
            <div className="flex items-start justify-between border-b border-slate-800 px-5 py-4">
              <div>
                <p className="text-lg font-semibold text-slate-100">{selectedRow.nombre}</p>
                <p className="text-xs text-slate-500">ID {selectedRow.id}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={selectedRow.status} />
                <button type="button" onClick={closeDetail} className="text-slate-400 hover:text-slate-200">
                  <FaTimes />
                </button>
              </div>
            </div>

            <div className="grid flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[1fr_320px]">
              <div className="overflow-y-auto p-5">
                {detailLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <FaSyncAlt className="animate-spin" />
                    Cargando detalle...
                  </div>
                ) : detailError ? (
                  <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">
                    {detailError}
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Plantilla</p>
                        <p className="mt-1 font-mono text-sm text-slate-100">{selectedRow.templateName}</p>
                      </div>

                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Lista</p>
                        <p className="mt-1 text-sm text-slate-100">{selectedRow.listName}</p>
                      </div>

                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Fecha de envío</p>
                        <p className="mt-1 text-sm text-slate-100">{selectedRow.schedule.dateLabel}</p>
                      </div>

                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Hora</p>
                        <p className="mt-1 text-sm text-slate-100">{selectedRow.schedule.timeLabel || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
                      <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Progreso de envío</p>

                      <div className="mb-4 grid grid-cols-3 gap-3 text-center">
                        <div>
                          <p className="text-xl font-bold text-slate-100">{selectedRow.total || selectedRow.sent || 0}</p>
                          <p className="text-xs text-slate-500">Total</p>
                        </div>
                        <div>
                          <p className="text-xl font-bold text-emerald-300">{selectedRow.sent}</p>
                          <p className="text-xs text-slate-500">Enviados</p>
                        </div>
                        <div>
                          <p className="text-xl font-bold text-rose-300">{selectedRow.failed}</p>
                          <p className="text-xs text-slate-500">Fallidos</p>
                        </div>
                      </div>

                      <ProgressBar
                        sent={selectedRow.sent}
                        total={selectedRow.total || selectedRow.sent || 1}
                        color={STATUS_STYLE[selectedRow.status]?.color || '#58a6ff'}
                      />
                    </div>

                    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
                      <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Corridas registradas</p>
                      {detailRuns.length === 0 ? (
                        <p className="text-sm text-slate-500">Sin corridas registradas para esta campaña.</p>
                      ) : (
                        <div className="space-y-2">
                          {detailRuns.map((r, idx) => (
                            <div key={`${r.run_id || 'default'}-${idx}`} className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2">
                              <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                                <span className="font-mono text-slate-300">{r.run_id || 'default'}</span>
                                <span className="text-slate-500">{Number(r.total || 0)} envíos</span>
                              </div>
                              <p className="mt-1 text-[11px] text-slate-500">
                                Inicio: {r.started_at ? new Date(r.started_at).toLocaleString('es-EC') : '-'}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end gap-2">
                      {selectedRow.status === 'scheduled' && (
                        <button
                          type="button"
                          onClick={async () => {
                            await removeCampaign(selectedRow.id);
                            closeDetail();
                          }}
                          className="inline-flex items-center gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-2 text-sm font-semibold text-rose-200"
                        >
                          <FaTrash size={12} />
                          Cancelar campaña
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={closeDetail}
                        className="rounded-lg border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-300"
                      >
                        Cerrar
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="overflow-y-auto border-t border-slate-800 bg-slate-900 p-5 lg:border-l lg:border-t-0">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Vista previa del mensaje</h3>

                <div className="overflow-hidden rounded-2xl border-4 border-slate-700">
                  <div className="flex items-center gap-2 bg-[#0b4f44] px-3 py-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-400 text-xs font-bold text-slate-900">
                      W
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-white">WaveSales</p>
                      <p className="text-[10px] text-emerald-100/80">Preview</p>
                    </div>
                  </div>
                  <div className="bg-[#0a1a15] p-3">
                    <div className="ml-auto max-w-[90%] rounded-xl bg-[#005c4b] px-3 py-2 text-xs text-white">
                      <p className="whitespace-pre-wrap break-words leading-relaxed">
                        {applySampleVars(selectedRow.templateBody || `Plantilla ${selectedRow.templateName}`)}
                      </p>
                      <p className="mt-1 text-right text-[10px] text-white/60">12:00 ✓✓</p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950 p-3 text-xs text-slate-400">
                  <div className="mb-2">
                    <p className="text-slate-500">Plantilla</p>
                    <p className="font-mono text-slate-200">{selectedRow.templateName}</p>
                  </div>
                  <div className="mb-2">
                    <p className="text-slate-500">Caracteres</p>
                    <p className="font-mono text-slate-200">{String(selectedRow.templateBody || '').length}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Variables detectadas</p>
                    <p className="font-mono text-emerald-300">
                      {(String(selectedRow.templateBody || '').match(/\{\{[^}]+\}\}/g) || []).join(', ') || 'Ninguna'}
                    </p>
                  </div>
                </div>

                {detailCampaignData && (
                  <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950 p-3 text-xs text-slate-400">
                    <p>Campaña backend: {detailCampaignData.nombre || '-'}</p>
                    <p>Lista backend: {detailCampaignData.lista_nombre || '-'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MensajesCoexistencia;
